import { isEqual } from 'lodash';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { MEDIA_MANAGER_WS_URL } from '@/config';
import { useMe } from '@/features/users/api/get-me';
import { User } from '@/features/users/types/types';
import keycloak from '@/lib/keycloak';

interface WebSocketMessage {
  section: string;
  resource_id?: string;
  workspace_id?: string;
  programs?: {
    [key: string]: {
      users: User[];
    };
  };
  segments?: {
    [key: string]: {
      users: User[];
    };
  };
  clips?: {
    [key: string]: {
      users: User[];
    };
  };
}

interface WebSocketContextType {
  sendMessage: (message: WebSocketMessage) => void;
  connectProgram: (programId: string) => void;
  disconnectProgram: (programId: string) => void;
  connectSegment: (programId: string) => void;
  disconnectSegment: (programId: string) => void;
  connectClip: (clipId: string) => void;
  disconnectClip: (clipId: string) => void;
  cleanLog: () => void;
  log: string;
  socketRef: React.MutableRefObject<WebSocket | null>;
}

export type PresenceMap = Record<string, string[]>;

type Category = 'program' | 'segment' | 'clip';

type PerCategory<T> = Record<Category, T>;

const CATEGORIES: Category[] = ['program', 'segment', 'clip'];

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

// Presence lives in one context per category, apart from the actions above.
// Every presence broadcast carries all three categories, so a single shared
// context would re-render every subscriber whenever *any* resource changed
// occupants — e.g. a reviewer walking through segments would re-render the
// broadcast schedule list, which throws away transient UI state there (an open
// date-range picker with an unapplied selection, most visibly). Splitting them
// keeps each subscriber tied to the category it actually paints.
const ProgramPresenceContext = createContext<PresenceMap | undefined>(undefined);
const SegmentPresenceContext = createContext<PresenceMap | undefined>(undefined);
const ClipPresenceContext = createContext<PresenceMap | undefined>(undefined);

// The backend stores presence with a 60-minute TTL and only refreshes it when it
// receives a `working_*` message. A tab must therefore re-assert the resource it
// occupies periodically, otherwise (a) presence expires mid-session on long
// reviews, and (b) a slot wrongly cleared by another tab closing or by a backend
// restart is never restored. Comfortably below the TTL.
const PRESENCE_HEARTBEAT_MS = 30_000;

// A broadcast that shows an occupied resource without our own user only
// reclaims it once our last `working_*` for that category is this old. It must
// stay well above the round trip of a `working_*` under load, so the broadcasts
// caused by our own `leaving_*` (which arrive before our `working_*` is
// processed) never look like a loss, and below the heartbeat, so it bounds how
// long a genuine loss can last.
const RECLAIM_MIN_INTERVAL_MS = 10_000;

// Same minimum validity the HTTP clients ask for in `src/lib/axios.ts`.
const MIN_TOKEN_VALIDITY_SECONDS = 60;

// Reconnect waits double from the base up to the cap; each wait is drawn
// between half and the whole of its step, so tabs dropped together by a server
// restart do not reconnect in lockstep.
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;

const WORKING_SECTION: PerCategory<string> = {
  program: 'working_program',
  segment: 'working_segment',
  clip: 'working_clip',
};

const LEAVING_SECTION: PerCategory<string> = {
  program: 'leaving_program',
  segment: 'leaving_segment',
  clip: 'leaving_clip',
};

const perCategory = <T,>(value: T): PerCategory<T> => ({
  program: value,
  segment: value,
  clip: value,
});

// Re-declare every resource this tab currently occupies. Reused on socket open,
// on the heartbeat, and when the tab regains focus/visibility.
const reassertWorkingResources = (
  assert: (category: Category, resourceId: string) => void,
  resources: PerCategory<string | null>
) => {
  CATEGORIES.forEach(category => {
    const resourceId = resources[category];
    if (resourceId) assert(category, resourceId);
  });
};

// A user is listed once per resource even when the broadcast repeats them, as
// it would if the backend stored presence per connection and the user had two
// tabs on the same resource.
const toPresenceMap = (entities: Record<string, { users: User[] }> | undefined): PresenceMap =>
  Object.fromEntries(
    Object.entries(entities || {}).map(([resourceId, value]) => [
      resourceId,
      [...new Set(value.users.map(user => user.id))],
    ])
  );

// The backend re-broadcasts the whole presence snapshot on every event, so most
// messages carry occupancy this client already knows (another tab merely
// connecting or disconnecting, a heartbeat re-assert, a change in a category
// nobody on screen paints). Returning the previous object in that case makes the
// state update a no-op for React and keeps subscribers from re-rendering.
const keepIfEqual = (next: PresenceMap) => (previous: PresenceMap) =>
  isEqual(previous, next) ? previous : next;

export const WebSocketProvider = ({ children }: { children: React.ReactNode }) => {
  const socketRef = useRef<WebSocket | null>(null);
  const messagesQueue = useRef<WebSocketMessage[]>([]);
  // Single source of truth for the resource this client currently occupies in
  // each category. The server tracks presence per (resource, user) and only
  // releases a slot on an explicit `leaving_*` (or on socket close). Keeping the
  // occupied id here lets us (a) guarantee at most one slot per category by
  // releasing the previous one on every connect, and (b) re-assert presence
  // after a reconnect, so a dropped socket never leaves stale/ghost slots.
  const workingResourcesRef = useRef<PerCategory<string | null>>(perCategory(null));
  // When this tab last sent `working_*` per category, the occupants of the
  // latest broadcast on the current socket, and the one pending reclaim
  // re-check per category.
  const lastAssertedAtRef = useRef<PerCategory<number | null>>(perCategory(null));
  const latestOccupantsRef = useRef<PerCategory<PresenceMap | null>>(perCategory(null));
  const reclaimTimersRef = useRef<PerCategory<ReturnType<typeof setTimeout> | null>>(
    perCategory(null)
  );
  // Reconnection state: whether the provider has unmounted, whether the last
  // socket ever opened (null before the first one), how many attempts have
  // failed in a row, and the one pending reconnect.
  const disposedRef = useRef(false);
  const openedRef = useRef<boolean | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Our own user id, kept in a ref so the (stable) onmessage closure can compare
  // it against the occupants in each presence broadcast.
  const currentUserIdRef = useRef<string | null>(null);
  const [userConnectedInPrograms, setUserConnectedInPrograms] = useState<PresenceMap>({});
  const [userConnectedInSegments, setUserConnectedInSegments] = useState<PresenceMap>({});
  const [userConnectedInClips, setUserConnectedInClips] = useState<PresenceMap>({});
  const [log, setLog] = useState('');

  const { data: me } = useMe();
  useEffect(() => {
    currentUserIdRef.current = me?.id ?? null;
  }, [me?.id]);

  const sendWorking = useCallback(
    (category: Category, resourceId: string, send: (message: WebSocketMessage) => void) => {
      lastAssertedAtRef.current[category] = Date.now();
      send({ section: WORKING_SECTION[category], resource_id: resourceId });
    },
    []
  );

  const cancelReclaim = useCallback((category: Category) => {
    const timer = reclaimTimersRef.current[category];
    if (timer) clearTimeout(timer);
    reclaimTimersRef.current[category] = null;
  }, []);

  // Reclaim the occupied resource of a category when the latest broadcast shows
  // it without our own user — but only once our lease is no longer fresh. A
  // younger lease defers to one re-check when it comes of age, so a genuine loss
  // is repaired within the interval while bursts of broadcasts never turn into
  // more than one message per interval.
  const reclaimIfMissing = useCallback(
    function reclaim(category: Category) {
      const ws = socketRef.current;
      const resourceId = workingResourcesRef.current[category];
      const occupants = latestOccupantsRef.current[category];
      const myId = currentUserIdRef.current;
      // A closed or still-connecting socket cannot carry it, and its `onopen`
      // re-asserts everything anyway.
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (!resourceId || !occupants || !myId || occupants[resourceId]?.includes(myId)) return;

      const age = Date.now() - (lastAssertedAtRef.current[category] ?? -Infinity);
      if (age >= RECLAIM_MIN_INTERVAL_MS) {
        sendWorking(category, resourceId, message => ws.send(JSON.stringify(message)));
        return;
      }
      if (reclaimTimersRef.current[category]) return;
      reclaimTimersRef.current[category] = setTimeout(() => {
        reclaimTimersRef.current[category] = null;
        reclaim(category);
      }, RECLAIM_MIN_INTERVAL_MS - age);
    },
    [sendWorking]
  );

  const connect = useCallback(
    async function connect(): Promise<void> {
      const scheduleReconnect = () => {
        if (disposedRef.current) return;
        if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
        const base = Math.min(
          RECONNECT_BASE_MS * 2 ** reconnectAttemptRef.current,
          RECONNECT_MAX_MS
        );
        reconnectAttemptRef.current += 1;
        reconnectTimerRef.current = setTimeout(
          () => {
            reconnectTimerRef.current = null;
            void connect();
          },
          base / 2 + Math.random() * (base / 2)
        );
      };

      const isLive = (ws: WebSocket | null) => !!ws && ws.readyState !== WebSocket.CLOSED;

      try {
        // A connecting, open or closing socket is reused; a closing one
        // schedules the next attempt from its own `onclose`.
        if (disposedRef.current || isLive(socketRef.current)) return;

        // The browser reports a refused handshake as a plain close, so when the
        // previous socket never opened the token it carried may have been
        // rejected however valid it looks here: force a new one.
        try {
          await keycloak.updateToken(openedRef.current === false ? -1 : MIN_TOKEN_VALIDITY_SECONDS);
        } catch {
          // Keycloak rejected the session: keycloak-js has cleared the token,
          // and under `onLoad: 'login-required'` its `clearToken()` is already
          // redirecting to login. Nothing left to reconnect with.
          if (!keycloak.token) return;
          // A transient failure: go on with the held token while it is valid.
          if (keycloak.isTokenExpired()) {
            scheduleReconnect();
            return;
          }
        }

        if (disposedRef.current || isLive(socketRef.current)) return;

        const ws = new WebSocket(
          `${MEDIA_MANAGER_WS_URL}/api/ws/v1/validator?token=${keycloak.token}`
        );
        socketRef.current = ws;
        openedRef.current = false;

        ws.onopen = () => {
          if (socketRef.current !== ws) return;
          openedRef.current = true;
          ws.send(JSON.stringify({ section: 'connected' }));
          // Re-assert whatever this client currently occupies. On a reconnect the
          // previous socket's close already released every slot server-side, so
          // without this the user's genuine current slot would silently disappear
          // for everyone until they navigated again.
          reassertWorkingResources(
            (category, resourceId) =>
              sendWorking(category, resourceId, message => ws.send(JSON.stringify(message))),
            workingResourcesRef.current
          );
          while (messagesQueue.current.length) {
            ws.send(JSON.stringify(messagesQueue.current.shift()));
          }
        };

        ws.onmessage = event => {
          if (socketRef.current !== ws) return;
          // The server answering is what proves the connection healthy; one that
          // accepts and drops at once must keep backing off.
          reconnectAttemptRef.current = 0;
          const data = JSON.parse(event.data);

          const occupants: PerCategory<PresenceMap> = {
            program: toPresenceMap(data.programs),
            segment: toPresenceMap(data.segments),
            clip: toPresenceMap(data.clips),
          };
          setUserConnectedInPrograms(keepIfEqual(occupants.program));
          setUserConnectedInSegments(keepIfEqual(occupants.segment));
          setUserConnectedInClips(keepIfEqual(occupants.clip));

          // Event-driven renewal: if a broadcast shows a resource this tab occupies
          // without our own user (another tab of ours closed and the backend released
          // every slot, the TTL lapsed, or the service restarted), reclaim it,
          // subject to the lease window.
          latestOccupantsRef.current = occupants;
          CATEGORIES.forEach(reclaimIfMissing);
        };

        ws.onerror = error => {
          console.error('⚠️ Error en WS:', error);
        };

        ws.onclose = event => {
          if (socketRef.current !== ws) return;
          // Pending re-checks and the occupants they would read belong to this
          // socket; the next one re-asserts on open.
          CATEGORIES.forEach(cancelReclaim);
          latestOccupantsRef.current = perCategory(null);
          console.warn('🔌 WS desconectado, intentando reconectar...', event.reason);
          scheduleReconnect();
        };
      } catch (error) {
        console.error('⚠️ Error al conectar el WS:', error);
        scheduleReconnect();
      }
    },
    [sendWorking, cancelReclaim, reclaimIfMissing]
  );

  useEffect(() => {
    disposedRef.current = false;
    void connect();
    return () => {
      disposedRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      CATEGORIES.forEach(cancelReclaim);
      const ws = socketRef.current;
      socketRef.current = null;
      ws?.close();
    };
  }, [connect, cancelReclaim]);

  const sendMessage = useCallback((msg: WebSocketMessage) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
    } else {
      messagesQueue.current.push(msg);
    }
  }, []);

  // Renew the lease on the currently occupied resources. Only while the socket
  // is open: if it is closed, `onopen` already re-asserts on reconnect, and we
  // must not enqueue duplicate heartbeats that would flush on the next connect.
  const renewPresence = useCallback(() => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) return;
    reassertWorkingResources(
      (category, resourceId) => sendWorking(category, resourceId, sendMessage),
      workingResourcesRef.current
    );
  }, [sendMessage, sendWorking]);

  useEffect(() => {
    const interval = setInterval(renewPresence, PRESENCE_HEARTBEAT_MS);
    // Coming back to a backgrounded tab is the moment presence is most likely to
    // be stale (another tab closed, TTL lapsed, service restarted), so renew now.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') renewPresence();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', renewPresence);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', renewPresence);
    };
  }, [renewPresence]);

  const occupy = useCallback(
    (category: Category, resourceId: string) => {
      const previous = workingResourcesRef.current[category];
      if (previous && previous !== resourceId) {
        sendMessage({ section: LEAVING_SECTION[category], resource_id: previous });
      }
      workingResourcesRef.current[category] = resourceId;
      cancelReclaim(category);
      sendWorking(category, resourceId, sendMessage);
    },
    [sendMessage, sendWorking, cancelReclaim]
  );

  const release = useCallback(
    (category: Category, resourceId: string) => {
      if (workingResourcesRef.current[category] === resourceId) {
        workingResourcesRef.current[category] = null;
        lastAssertedAtRef.current[category] = null;
        cancelReclaim(category);
      }
      sendMessage({ section: LEAVING_SECTION[category], resource_id: resourceId });
    },
    [sendMessage, cancelReclaim]
  );

  const connectProgram = useCallback((programId: string) => occupy('program', programId), [occupy]);
  const disconnectProgram = useCallback(
    (programId: string) => release('program', programId),
    [release]
  );
  const connectSegment = useCallback((segmentId: string) => occupy('segment', segmentId), [occupy]);
  const disconnectSegment = useCallback(
    (segmentId: string) => release('segment', segmentId),
    [release]
  );
  const connectClip = useCallback((clipId: string) => occupy('clip', clipId), [occupy]);
  const disconnectClip = useCallback((clipId: string) => release('clip', clipId), [release]);

  const cleanLog = useCallback(() => setLog(''), []);

  const actions = useMemo<WebSocketContextType>(
    () => ({
      sendMessage,
      connectProgram,
      disconnectProgram,
      connectSegment,
      disconnectSegment,
      connectClip,
      disconnectClip,
      cleanLog,
      log,
      socketRef,
    }),
    [
      sendMessage,
      connectProgram,
      disconnectProgram,
      connectSegment,
      disconnectSegment,
      connectClip,
      disconnectClip,
      cleanLog,
      log,
    ]
  );

  return (
    <WebSocketContext.Provider value={actions}>
      <ProgramPresenceContext.Provider value={userConnectedInPrograms}>
        <SegmentPresenceContext.Provider value={userConnectedInSegments}>
          <ClipPresenceContext.Provider value={userConnectedInClips}>
            {children}
          </ClipPresenceContext.Provider>
        </SegmentPresenceContext.Provider>
      </ProgramPresenceContext.Provider>
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

const usePresence = (context: React.Context<PresenceMap | undefined>, hookName: string) => {
  const presence = useContext(context);
  if (!presence) {
    throw new Error(`${hookName} must be used within a WebSocketProvider`);
  }
  return presence;
};

/** Users currently working on each program, keyed by program id. */
export const useProgramPresence = (): PresenceMap =>
  usePresence(ProgramPresenceContext, 'useProgramPresence');

/** Users currently working on each segment, keyed by segment id. */
export const useSegmentPresence = (): PresenceMap =>
  usePresence(SegmentPresenceContext, 'useSegmentPresence');

/** Users currently working on each clip, keyed by clip id. */
export const useClipPresence = (): PresenceMap =>
  usePresence(ClipPresenceContext, 'useClipPresence');
